import { Lightning } from '@lightningjs/sdk'

import { getColor, grey960, transparent } from '../../constants/colors'
import { UniNeueBold } from '../../constants/fonts'
import { AppConfig } from '../../services/appConfig'
import { TvSpotlightModelStylesEnum } from '../../types/flo-enums'
import { CardItemModel, TvBrowseLayoutModel } from '../../types/flo-experience'
import { Flo } from '../../types/flo-live-api'
import { isLowEndDevice } from '../../utils/info'
import { debounce } from '../../utils/misc'
import PointerBackButton from '../NavigationPointers/PointerBackButton'
import Spotlight from '../spotlight/spotlight'
import TvCarouselContainer from '../TvCarouselContainer/TvCarouselContainer'
import TvRowsContainer from '../TvRowsContainer/TvRowsContainer'

export enum BrowseLayoutStates {
  TvRowsContainer = 'TvRowsContainer',
  SpotlightMetadata = 'SpotlightMetadata',
}

const DEFAULT_SPOTLIGHT_OFFSET = 440
const DEFAULT_CAROUSELS_OFFSET = 504
const EVENT_HUB_SPOTLIGHT_OFFSET = 268
const EVENT_HUB_CAROUSELS_OFFSET = 332
const FAVORITE_HUB_SPOTLIGHT_OFFSET = 320
const FAVORITE_HUB_CAROUSELS_OFFSET = 394
const DELAY = isLowEndDevice() ? 3000 : 5000

const TOP_SAFE_PADDING = 80
const TITLE_HEIGHT = 108
const TITLE_SAFE_MARGIN = 80
const NAV_HUB_HEADER_HEIGHT = TITLE_HEIGHT + TOP_SAFE_PADDING + TITLE_SAFE_MARGIN
const NAV_HUB_HEADER_WIDTH = 1984
const NO_SPOTLIGHT_OFFSET = 48

export default class TvBrowseLayout extends Lightning.Component {
  private _data: TvBrowseLayoutModel
  private updateSpotlightWithDelay: any
  private _isNavHub = false
  private _isFavoriteHub = false
  private _isEventHub = false
  private _hasSpotlight = true

  static _template() {
    return {
      w: (w) => w,
      h: (h) => h,
      rect: true,
      color: transparent,
      NavHubHeader: {
        visible: false,
        zIndex: 2,
        flex: {
          direction: 'row',
        },

        Gradient: {
          flexItem: false,
          flex: {
            direction: 'column',
          },
          w: NAV_HUB_HEADER_WIDTH,
          x: -64,
          h: NAV_HUB_HEADER_HEIGHT,

          GradientTop: {
            rect: true,
            h: TOP_SAFE_PADDING + TITLE_HEIGHT,
            color: getColor(grey960),
          },
          GradientBottom: {
            rect: true,
            h: TITLE_SAFE_MARGIN / 2,
            colorTop: getColor(grey960),
            colorBottom: transparent,
          },
        },
        Title: {
          y: 94,
          text: {
            text: '',
            fontSize: 48,
            fontFace: UniNeueBold,
            lineHeight: 56,
          },
        },
      },
      PointerBackButton: {
        type: PointerBackButton,
        x: 1648,
      },
      Spotlight: {
        w: (w) => w,
        h: (h) => h,
        type: Spotlight,
        offset: DEFAULT_SPOTLIGHT_OFFSET,
      },
      TvRowsContainer: {
        type: TvRowsContainer,
        withSpotlight: true,
        offset: DEFAULT_CAROUSELS_OFFSET,
      },
    }
  }

  get navHubHeader() {
    return this.tag('NavHubHeader')
  }

  get gradientTop() {
    return this.tag('GradientTop')
  }

  get title() {
    return this.tag('Title')
  }

  get spotlight(): Spotlight {
    return this.tag('Spotlight') as Spotlight
  }

  get tvRowsContainer() {
    return this.tag('TvRowsContainer') as TvRowsContainer
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  get hasSpotlight() {
    return this._hasSpotlight
  }

  set isEventHub(v: boolean) {
    this._isEventHub = v
    this.spotlight.offset = v ? EVENT_HUB_SPOTLIGHT_OFFSET : DEFAULT_SPOTLIGHT_OFFSET
    this.tvRowsContainer.offset = v ? EVENT_HUB_CAROUSELS_OFFSET : DEFAULT_CAROUSELS_OFFSET
  }

  get isEventHub() {
    return this._isEventHub
  }

  set isNavHub(v: boolean) {
    this._isNavHub = v
    this.spotlight.visible = !v
    this.spotlight.toggleLogo(!v)
    this.tvRowsContainer.offset = v ? NAV_HUB_HEADER_HEIGHT : DEFAULT_CAROUSELS_OFFSET
  }

  set isFavoriteHub(v: boolean) {
    this._isFavoriteHub = v
    this.spotlight.toggleLogo(!v)
    this.spotlight.offset = v ? FAVORITE_HUB_SPOTLIGHT_OFFSET : DEFAULT_SPOTLIGHT_OFFSET
    this.tvRowsContainer.offset = v ? FAVORITE_HUB_CAROUSELS_OFFSET : DEFAULT_CAROUSELS_OFFSET
  }

  get isFavoriteHub() {
    return this._isFavoriteHub
  }

  set data(v: TvBrowseLayoutModel) {
    this._data = v
    this.tvRowsContainer.data = v?.container?.items

    if (!v?.spotlight) {
      this.spotlight.visible = false
      this.tvRowsContainer.offset = NO_SPOTLIGHT_OFFSET
      this._hasSpotlight = false
      return
    }

    if (v?.spotlight?.style === TvSpotlightModelStylesEnum.NavHub) {
      this.isNavHub = true
      this.navHubHeader.patch({
        visible: true,
        Title: { text: { text: v?.spotlight?.title?.text } },
      })
    }
  }

  get data() {
    return this._data
  }

  get curCardStreamId() {
    return this.tvRowsContainer?.currentContainer?._getFocused().data?.spotlight_metadata
      ?.background_preview_metadata?.stream_id
  }

  static _states() {
    return [
      class TvRowsContainer extends this {
        _getFocused() {
          return this.tvRowsContainer
        }
      },

      class SpotlightMetadata extends this {
        _getFocused() {
          return this.spotlight.metadata
        }

        _handleDown() {
          this._setState(BrowseLayoutStates.TvRowsContainer)
        }
      },
    ]
  }

  _init() {
    this.updateSpotlightWithDelay = debounce(this.updateSpotlight.bind(this), DELAY)
    this._setState(BrowseLayoutStates.TvRowsContainer)
  }

  _inactive() {
    this.updateSpotlightWithDelay.cancel()
  }

  _handleUp() {
    if (this.spotlight.isFocusable) {
      this._setState(BrowseLayoutStates.SpotlightMetadata)
    }
  }

  $toggleSpotlightVisibility(toggle: boolean) {
    if (this._hasSpotlight) {
      this.spotlight.visible = !!toggle
    }
  }

  $cancelSpotlightTimeout() {
    this.cancelSpotlight()
  }

  async $updateSpotlight(data: CardItemModel) {
    if (!this._hasSpotlight) return

    this.updateSpotlightWithDelay.cancel()

    if (this._isNavHub || this._isFavoriteHub) {
      this.spotlight.clearLivePreview()
      this.spotlight.toggleLogo(false)
      return
    }

    // When the spotlight metadata is empty we don't play any live preview
    // but we keep the component
    this.spotlight.renderSpotlightData(data)

    const hasSpotlightMetadata = data?.spotlight_metadata?.background_preview_metadata

    if (!hasSpotlightMetadata) {
      this.spotlight.clearLivePreview()
      return
    }

    // If set to true we need to do the geo check request before playing anything
    const checkForGeoRestriction =
      data?.spotlight_metadata?.background_preview_metadata?.check_geo_restriction

    const liveId =
      data?.action?.analytics?.extra_context?.live_id ||
      data?.spotlight_metadata?.background_preview_video_analytics_metadata?.event_id

    let isAllowed = true

    // We need to make sure live/event ID exists or this will throw an error
    if (checkForGeoRestriction && liveId) {
      isAllowed = await AppConfig.shared.liveService.checkGeoRestriction({ liveId })
    }

    if (!isAllowed) {
      this.spotlight.clearLivePreview()
      return
    }

    const streamId = data?.spotlight_metadata?.background_preview_metadata?.stream_id
    const stream = await AppConfig.shared.liveService.getPremiumStreamPreviewToken({ streamId })

    this.updateSpotlightWithDelay(stream, data)
  }

  async updateSpotlight(stream: Flo.Live.PremiumStreamResponse, data: CardItemModel) {
    const currentFocus = this._getFocused()

    if (
      currentFocus instanceof TvRowsContainer &&
      currentFocus.currentContainer instanceof TvCarouselContainer &&
      this.tvRowsContainer.currentCarouselIndex === 0
    ) {
      this.spotlight.startPreview(stream, data)
    }
  }

  cancelSpotlight() {
    this.updateSpotlightWithDelay.cancel()
    this.spotlight.clearLivePreview()
  }
}
